import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Camera, Heart, Loader2, MapPin, MessageCircle, Sparkles, Trash2 } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CommentThread } from "@/components/CommentThread";
import { api, errorMessage } from "@/lib/api";
import { imgSrc } from "@/lib/categories";
import { uploadImage } from "@/lib/upload";

export function PhotoHeart({ photo, canLike, onLiked, tid }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { const { data } = await api.post("/photos/like", { url: photo.url }); onLiked(photo.url, data); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const inner = <><Heart className={`h-3.5 w-3.5 ${photo.liked ? "fill-[#FF3B6B] text-[#FF3B6B]" : ""}`} />{photo.like_count || 0}</>;
  if (!canLike) return <span data-testid={`${tid}-count`} className="flex items-center gap-0.5 shrink-0">{inner}</span>;
  return (
    <span role="button" tabIndex={0} data-testid={tid} aria-label="Heart photo" onClick={busy ? undefined : toggle} onKeyDown={(e) => e.key === "Enter" && toggle(e)}
      className="flex items-center gap-0.5 shrink-0 rounded-full bg-[#0B0F19]/70 px-1.5 py-0.5 hover:text-[#FF3B6B] transition-colors cursor-pointer">{inner}</span>
  );
}

function UpgradePhotos({ total }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/photo-addon", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.success("Photo add-on active — you can now post up to 20 photos"); window.location.reload(); }
    } catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  return (
    <Button data-testid="photo-addon-button" disabled={busy} onClick={go} variant="outline" className="rounded-xl border-[#FFB800]/50 text-[#FFB800] bg-transparent hover:bg-[#FFB800]/10">
      <Sparkles className="h-4 w-4 mr-1.5" /> Post up to 20 photos · +$3/mo (${total}/mo total)
    </Button>
  );
}

function PostPhoto({ onPosted, used, limit, canUpgrade, addonTotal }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async () => {
    setBusy(true);
    try {
      const url = await uploadImage(api, file);
      await api.post("/profile/photos", { url, caption, city });
      toast.success("Photo posted"); setFile(null); setCaption(""); setCity(""); onPosted();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const full = used >= limit;
  return (
    <div data-testid="post-photo" className="mb-5 rounded-2xl bg-[#151C2C] border border-[#26334D] p-4 flex flex-wrap items-center gap-3">
      <span data-testid="photo-usage" className="text-xs text-slate-400 w-full">{used} of {limit} profile photos used</span>
      {full ? (canUpgrade ? <UpgradePhotos total={addonTotal} /> : <span className="text-sm text-slate-400">You've reached your photo limit. Delete one to post another.</span>) : <>
      <button type="button" data-testid="post-photo-pick" onClick={() => ref.current?.click()}
        className="h-12 px-4 rounded-xl border border-dashed border-[#26334D] hover:border-[#FFB800] flex items-center gap-2 text-sm text-slate-300 transition-colors">
        <Camera className="h-4 w-4 text-[#FFB800]" /> {file ? file.name.slice(0, 20) : "Post a photo"}
      </button>
      <input ref={ref} type="file" accept="image/*" hidden data-testid="post-photo-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      {file && <>
        <input data-testid="post-photo-caption" value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200} placeholder="Add a caption (optional)"
          className="flex-1 min-w-[10rem] bg-[#0B0F19] border border-[#26334D] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FFB800]" />
        <input data-testid="post-photo-city" value={city} onChange={(e) => setCity(e.target.value)} maxLength={120} placeholder="City (optional), e.g. Orlando, FL"
          className="w-48 bg-[#0B0F19] border border-[#26334D] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FFB800]" />
        <Button data-testid="post-photo-submit" disabled={busy} onClick={post} className="rounded-xl bg-[#FFB800] hover:bg-[#E6A600] text-[#0B0F19] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
        </Button>
      </>}
      </>}
      {!full && canUpgrade && used >= limit - 2 && <UpgradePhotos total={addonTotal} />}
    </div>
  );
}

export function PhotoDialog({ photo, canComment, canLike, isMe, onClose, onDeleted, onLiked, author, showComments = true }) {
  const remove = async () => {
    try { await api.delete(`/profile/photos/${photo.post_id}`); toast.success("Photo deleted"); onDeleted(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={!!photo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="photo-dialog" className="max-w-2xl bg-[#111827] border-[#26334D] text-white max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">Photo</DialogTitle>
        {photo && <>
          <img src={imgSrc(photo.url)} alt={photo.caption || photo.place_name || "Photo"} className="w-full max-h-[55vh] object-contain rounded-xl bg-black" />
          {author && (
            <Link to={`/traveler/${author.id}`} data-testid="photo-dialog-author" onClick={onClose} className="flex items-center gap-2 text-sm font-semibold hover:text-[#FFB800]">
              <Avatar url={author.avatar_url} name={author.name} size="h-7 w-7" /> {author.name}
            </Link>
          )}
          <div className="flex items-start justify-between gap-3">
            <p data-testid="photo-dialog-caption" className="text-sm text-slate-200 flex-1">{photo.caption || photo.place_name}</p>
            <PhotoHeart photo={photo} canLike={canLike} onLiked={onLiked} tid="photo-dialog-like" />
            {isMe && photo.post_id && <button data-testid="photo-delete" onClick={remove} aria-label="Delete photo"><Trash2 className="h-4 w-4 text-[#FF7A7A]" /></button>}
          </div>
          {photo.city && <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{photo.city}</p>}
          {showComments ? <CommentThread kind="photo" target={photo.url} count={photo.comment_count} defaultOpen canComment={canComment} tid="photo" />
            : <p data-testid="photo-dialog-follow-hint" className="text-xs text-slate-500">Follow {author?.name || "this traveler"} to see comments and heart their photos.</p>}
        </>}
      </DialogContent>
    </Dialog>
  );
}

export function ProfilePhotos({ p, reload }) {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(null);
  const [photos, setPhotos] = useState(p.photos);
  const [sort, setSort] = useState("loved");
  useEffect(() => { setPhotos(p.photos); }, [p.photos]);
  const sorted = useMemo(() => [...photos].sort((a, b) => (sort === "loved" ? (b.like_count || 0) - (a.like_count || 0) : 0)
    || (b.created_at || "").localeCompare(a.created_at || "")), [photos, sort]);
  const onLiked = (url, data) => {
    setPhotos((xs) => xs.map((x) => (x.url === url ? { ...x, ...data } : x)));
    setOpen((o) => (o && o.url === url ? { ...o, ...data } : o));
  };
  const deep = params.get("photo");
  useEffect(() => { if (deep) setOpen(p.photos.find((ph) => ph.url === deep) || null); }, [deep, p.photos]);
  const close = () => { setOpen(null); if (deep) setParams({}, { replace: true }); };

  return (
    <div>
      {p.is_me && <PostPhoto onPosted={reload} used={p.photo_posts} limit={p.photo_limit} canUpgrade={p.can_upgrade_photos} addonTotal={p.photo_addon_total} />}
      {photos.length > 1 && (
        <div className="mb-4 inline-flex p-1 rounded-full bg-[#151C2C] border border-[#26334D]">
          {[["loved", "Most loved"], ["newest", "Newest"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`photo-sort-${v}`} onClick={() => setSort(v)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${sort === v ? "bg-white text-[#0B0F19] font-semibold" : "text-slate-300 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      )}
      {p.photos.length === 0 && <p data-testid="profile-photos-empty" className="text-sm text-slate-500 rounded-2xl border border-dashed border-[#26334D] p-10 text-center">No photos yet.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {sorted.map((ph, i) => (
          <button key={ph.post_id || `rv-${ph.review_id}-${i}`} type="button" onClick={() => setOpen(ph)} data-testid={`profile-photo-${i}`} className="group relative aspect-square rounded-2xl overflow-hidden border border-[#26334D] text-left">
            <img src={imgSrc(ph.url)} alt={ph.caption || ph.place_name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0B0F19] to-transparent p-2 text-xs font-semibold flex items-end justify-between gap-2">
              <span className="truncate">{ph.caption || ph.place_name}</span>
              <span className="flex items-center gap-2 shrink-0">
                {ph.comment_count > 0 && <span className="flex items-center gap-0.5"><MessageCircle className="h-3 w-3" />{ph.comment_count}</span>}
                <PhotoHeart photo={ph} canLike={p.can_like} onLiked={onLiked} tid={`photo-like-${i}`} />
              </span>
            </span>
          </button>
        ))}
      </div>
      <PhotoDialog photo={open} canComment={p.can_comment} canLike={p.can_like} onLiked={onLiked} isMe={p.is_me} onClose={close} onDeleted={() => { close(); reload(); }} />
    </div>
  );
}
